import React, { useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import Modal from "react-bootstrap/Modal";
import ProgressBar from "react-bootstrap/ProgressBar";
import Spinner from "react-bootstrap/Spinner";
import { useTranslation } from "react-i18next";

import ConfirmModal from "../confirmModal/confirmModel";
import {
  getDeviceIssueItems,
  getPendingDevicePrimaryLabel,
  getPendingDeviceSecondaryLabel,
  pendingDeviceKey,
} from "../../helpers/eventSummaryReadiness";
import "./eventSummaryPendingDevicesModal.css";

/**
 * @param {{
 *   show: boolean;
 *   onHide: () => void;
 *   readiness: {
 *     canDownload?: boolean;
 *     requiredDeviceCount?: number;
 *     readyDeviceCount?: number;
 *     pendingDevices?: Array<{
 *       loginId?: string;
 *       deviceId?: string;
 *       operatorName?: string;
 *       issues?: string[];
 *     }>;
 *   } | null;
 *   onRetry: () => void;
 *   onForceClose: (device: { loginId: string; deviceId: string }, reason: "lost" | "unavailable" | "force") => void;
 *   retrying?: boolean;
 *   forceClosingKey?: string | null;
 * }} props
 */
const EventSummaryPendingDevicesModal = ({
  show,
  onHide,
  readiness,
  onRetry,
  onForceClose,
  retrying = false,
  forceClosingKey = null,
}) => {
  const { t } = useTranslation();
  const [lostConfirmDevice, setLostConfirmDevice] = useState(null);

  const pendingDevices = Array.isArray(readiness?.pendingDevices)
    ? readiness.pendingDevices
    : [];

  const readyCount = readiness?.readyDeviceCount ?? 0;
  const requiredCount = readiness?.requiredDeviceCount ?? 0;
  const progressPct = useMemo(() => {
    if (!requiredCount) return 0;
    return Math.min(100, Math.round((readyCount / requiredCount) * 100));
  }, [readyCount, requiredCount]);

  const handleForceCloseClick = (device, reason) => {
    if (reason === "lost") {
      setLostConfirmDevice(device);
      return;
    }
    onForceClose(device, reason);
  };

  const confirmLostForceClose = () => {
    if (!lostConfirmDevice) return;
    onForceClose(lostConfirmDevice, "lost");
    setLostConfirmDevice(null);
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="lg"
        className="event-summary-pending-modal"
        scrollable
      >
        <Modal.Header closeButton className="event-summary-pending-modal__header">
          <Modal.Title>{t("EVENT_SUMMARY_PENDING_MODAL_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="event-summary-pending-modal__body">
          <div className="event-summary-pending-alert">
            {t("EVENT_SUMMARY_PENDING_MODAL_MESSAGE")}
          </div>

          {readiness ? (
            <div className="event-summary-pending-progress">
              <div className="event-summary-pending-progress__label">
                <span>{t("EVENT_SUMMARY_PENDING_DEVICE_COUNTS", {
                  ready: readyCount,
                  required: requiredCount,
                })}</span>
                <span className="event-summary-pending-progress__pct">
                  {progressPct}%
                </span>
              </div>
              <ProgressBar
                now={progressPct}
                variant={progressPct >= 100 ? "success" : "warning"}
                className="event-summary-pending-progress__bar"
              />
            </div>
          ) : null}

          {pendingDevices.length === 0 ? (
            <p className="event-summary-pending-empty mb-0">
              {t("EVENT_SUMMARY_PENDING_DEVICES_EMPTY")}
            </p>
          ) : (
            <ul className="event-summary-device-list list-unstyled mb-0">
              {pendingDevices.map((device) => {
                const key = pendingDeviceKey(device);
                const isClosing = forceClosingKey === key;
                const primaryLabel = getPendingDevicePrimaryLabel(device);
                const secondaryLabel = getPendingDeviceSecondaryLabel(device);
                const issueItems = getDeviceIssueItems(device.issues, t);

                return (
                  <li key={key} className="event-summary-device-card">
                    <div className="event-summary-device-card__main">
                      <div className="event-summary-device-card__operator">
                        <span className="event-summary-device-card__label">
                          {t("EVENT_SUMMARY_COL_OPERATOR_NAME")}
                        </span>
                        <span className="event-summary-device-card__title">
                          {primaryLabel}
                        </span>
                      </div>
                      {secondaryLabel ? (
                        <div
                          className="event-summary-device-card__meta"
                          title={device.deviceId || undefined}
                        >
                          {secondaryLabel}
                        </div>
                      ) : null}
                      <div className="event-summary-device-card__issues">
                        {issueItems.map((issue) => (
                          <span
                            key={`${key}-${issue.code}`}
                            className={`event-summary-issue-badge ${issue.badgeClass}`}
                          >
                            {issue.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="event-summary-device-card__action">
                      <Dropdown align="end">
                        <Dropdown.Toggle
                          variant="outline-light"
                          size="sm"
                          className="event-summary-force-close-btn"
                          disabled={isClosing || retrying}
                          id={`force-close-${key}`}
                        >
                          {isClosing ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-1"
                              />
                              {t("EVENT_SUMMARY_FORCE_CLOSING")}
                            </>
                          ) : (
                            t("EVENT_SUMMARY_FORCE_CLOSE")
                          )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          align="end"
                          className="event-summary-force-close-menu"
                        >
                          <Dropdown.Item
                            onClick={() =>
                              handleForceCloseClick(device, "lost")
                            }
                          >
                            {t("EVENT_SUMMARY_MARK_AS_LOST")}
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleForceCloseClick(device, "unavailable")
                            }
                          >
                            {t("EVENT_SUMMARY_MARK_UNAVAILABLE")}
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleForceCloseClick(device, "force")
                            }
                          >
                            {t("EVENT_SUMMARY_FORCE_CLOSE_DEVICE")}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer className="event-summary-pending-modal__footer">
          <Button
            variant="outline-secondary"
            className="borderButton"
            onClick={onHide}
          >
            {t("CANCEL")}
          </Button>
          <Button variant="primary" onClick={onRetry} disabled={retrying}>
            {retrying ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t("EVENT_SUMMARY_RETRYING")}
              </>
            ) : (
              t("EVENT_SUMMARY_RETRY")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        showModal={Boolean(lostConfirmDevice)}
        onHide={() => setLostConfirmDevice(null)}
        rightButtonClick={() => setLostConfirmDevice(null)}
        modalTitle={t("EVENT_SUMMARY_LOST_CONFIRM_TITLE")}
        modalDescription={t("EVENT_SUMMARY_LOST_CONFIRM_MESSAGE", {
          deviceLabel: lostConfirmDevice
            ? getPendingDevicePrimaryLabel(lostConfirmDevice)
            : "",
        })}
        leftBtnTitle={t("EVENT_SUMMARY_MARK_AS_LOST")}
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={confirmLostForceClose}
        isFirstButton
        isSecondButton
        leftBtnClass="ok_Btn"
        rightBtnClass="borderButton"
        isUnderline={false}
        note
        noteText={t("EVENT_SUMMARY_LOST_CONFIRM_NOTE")}
      />
    </>
  );
};

export default EventSummaryPendingDevicesModal;
